/** @jsxImportSource @emotion/react */
/**
 * PaymentRequiredBanner Component
 * SESSION_332: Stripe Auto-Charge on Approval - Limited Access UI
 *
 * Displays a prominent banner when a user's business is approved but they
 * haven't completed subscription payment. Uses a warm amber/gold theme
 * to indicate action is needed without alarm.
 *
 * Features:
 * - Warm amber/gold gradient (distinct from pending blue and rejected red)
 * - Clear messaging about completing payment to access mentions
 * - Prominent CTA button to navigate to payment
 * - Feature preview badges showing what they'll unlock
 * - Accessible color contrast and focus states
 */

import React from 'react';
import { Box, Group, Stack, Loader } from '@mantine/core';
import {
  IconCreditCard,
  IconArrowRight,
  IconChartBar,
  IconBell,
  IconSparkles
} from '@tabler/icons-react';
import type { PaymentRequiredBannerProps } from './PaymentRequiredBanner.types';
import {
  bannerContainerStyles,
  paymentBannerStyles,
  contentWrapperStyles,
  geometricAccentStyles,
  geometricAccentSecondaryStyles,
  geometricAccentTertiaryStyles,
  iconContainerStyles,
  titleStyles,
  messageStyles,
  businessNameStyles,
  accentLineContainerStyles,
  accentLineShimmerStyles,
  ctaButtonStyles,
  featureListStyles,
  featureBadgeStyles
} from './PaymentRequiredBanner.styles';

/**
 * PaymentRequiredBanner - Prompts users to complete subscription payment
 */
export const PaymentRequiredBanner: React.FC<PaymentRequiredBannerProps> = ({
  businessName,
  onCompletePayment,
  isLoading = false
}): React.ReactElement => {
  return (
    <Box
      css={[bannerContainerStyles, paymentBannerStyles]}
      role="alert"
      aria-live="polite"
    >
      {/* Geometric accent shapes */}
      <Box css={geometricAccentStyles} aria-hidden="true" />
      <Box css={geometricAccentSecondaryStyles} aria-hidden="true" />
      <Box css={geometricAccentTertiaryStyles} aria-hidden="true" />

      <Box css={contentWrapperStyles}>
        <Group gap="md" align="flex-start" wrap="nowrap">
          {/* Icon */}
          <Box css={iconContainerStyles}>
            <IconCreditCard size={26} color="#ffffff" />
          </Box>

          {/* Content */}
          <Stack gap={8} style={{ flex: 1 }}>
            <h3 css={titleStyles}>Complete Your Subscription</h3>
            <p css={messageStyles}>
              Great news! <span css={businessNameStyles}>{businessName}</span> has been
              approved. Complete your subscription to start tracking mentions and unlock
              all features.
            </p>

            {/* Feature badges - what they'll unlock */}
            <Box css={featureListStyles}>
              <span css={featureBadgeStyles}>
                <IconChartBar size={14} />
                Mention Analytics
              </span>
              <span css={featureBadgeStyles}>
                <IconBell size={14} />
                Real-time Alerts
              </span>
              <span css={featureBadgeStyles}>
                <IconSparkles size={14} />
                AI Insights
              </span>
            </Box>

            {/* Shimmer accent line */}
            <Box css={accentLineContainerStyles}>
              <Box css={accentLineShimmerStyles} />
            </Box>

            {/* CTA Button */}
            <button
              css={ctaButtonStyles}
              onClick={onCompletePayment}
              disabled={isLoading}
              type="button"
              aria-label="Complete subscription payment"
            >
              {isLoading ? (
                <Loader size={16} color="#B45309" />
              ) : (
                <>
                  Complete Payment
                  <IconArrowRight size={18} />
                </>
              )}
            </button>
          </Stack>
        </Group>
      </Box>
    </Box>
  );
};
